import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Box from '@material-ui/core/Box';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import {ANALYTICS_CHART_COLORS} from 'helpers/constants/analytics';
import roundTheNumber from 'helpers/functionUtils/roundTheNumber';
import {SauceChart} from 'routes/App/dashboard/styles';

const GROSS_COLOR = ANALYTICS_CHART_COLORS.salesCountGross;
const ORDERS_COLOR = ANALYTICS_CHART_COLORS.salesCountOrders;

const formatBucketLabel = (bucketStart, granularity) =>
  granularity === 'hour'
    ? moment.parseZone(bucketStart).format('h A')
    : moment.parseZone(bucketStart).format('M/D');

const formatBucketTooltip = (bucketStart, granularity) =>
  granularity === 'hour'
    ? moment.parseZone(bucketStart).format('MMM D, h A')
    : moment.parseZone(bucketStart).format('MMM D, YYYY');

/**
 * Gross sales ($, left axis) + orders (count, right axis) over the selected
 * period. The BFF picks the bucket granularity: days for multi-day periods,
 * hours when a single day is selected (GROW-2435).
 */
const SalesCountChart = ({series}) => {
  const granularity = series?.granularity ?? 'day';

  const chartData = useMemo(
    () =>
      (series?.points ?? []).map((point) => ({
        label: formatBucketLabel(point.bucketStart, granularity),
        info: formatBucketTooltip(point.bucketStart, granularity),
        grossSales: point.grossSales,
        orderVolume: point.orderVolume
      })),
    [series, granularity]
  );

  const CustomTooltip = ({active, payload}) => {
    if (!active || !payload?.length) {
      return null;
    }
    const row = payload[0].payload;
    return (
      <Box className='ChartTooltip'>
        <p>{row.info}</p>
        <p>{`Gross: $${roundTheNumber(row.grossSales)}`}</p>
        <p>{`Orders: ${row.orderVolume}`}</p>
      </Box>
    );
  };

  CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(PropTypes.object)
  };

  CustomTooltip.defaultProps = {
    active: false,
    payload: []
  };

  if (!chartData.length) {
    return null;
  }

  return (
    <SauceChart>
      <div>
        <ResponsiveContainer>
          <LineChart data={chartData}>
            <CartesianGrid
              strokeDasharray='0'
              strokeWidth='1'
              stroke='#E7E7E7'
              vertical={false}
            />
            <XAxis
              dataKey='label'
              axisLine={false}
              tickLine={false}
              tick={{fontSize: 14, fill: '#666'}}
            />
            <YAxis
              yAxisId='gross'
              axisLine={false}
              tickLine={false}
              tick={{fontSize: 14, fill: '#666'}}
              tickFormatter={(value) => `$${value}`}
            />
            <YAxis
              yAxisId='orders'
              orientation='right'
              axisLine={false}
              tickLine={false}
              tick={{fontSize: 14, fill: '#666'}}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend verticalAlign='top' height={36} iconType='rect' />
            <Line
              yAxisId='gross'
              type='monotone'
              dataKey='grossSales'
              name='Gross'
              stroke={GROSS_COLOR}
              strokeWidth={2}
              dot={{r: 2, fill: GROSS_COLOR}}
            />
            <Line
              yAxisId='orders'
              type='monotone'
              dataKey='orderVolume'
              name='Orders'
              stroke={ORDERS_COLOR}
              strokeWidth={2}
              dot={{r: 2, fill: ORDERS_COLOR}}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </SauceChart>
  );
};

SalesCountChart.defaultProps = {
  series: null
};

SalesCountChart.propTypes = {
  series: PropTypes.shape({
    granularity: PropTypes.oneOf(['day', 'hour']),
    points: PropTypes.arrayOf(
      PropTypes.shape({
        bucketStart: PropTypes.string,
        grossSales: PropTypes.number,
        orderVolume: PropTypes.number
      })
    )
  })
};

export default SalesCountChart;
